<script lang="ts">
import { defineComponent } from 'vue';
import algoliasearch from 'algoliasearch/lite';

import {
  AisHits,
  AisInstantSearch,
  AisSearchBox,
  AisStateResults,
  AisStats,
} from 'vue-instantsearch/vue3/es';

export default defineComponent({
  components: {
    AisHits,
    AisInstantSearch,
    AisSearchBox,
    AisStateResults,
    AisStats,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    environment: {
      type: String,
      required: true,
    },
    isDesktop: {
      type: Boolean,
      required: true,
    },
    /**
     * Desktop is always enabled, mobile is enabled based on the trigger icon
     */
    isMobileEnabled: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['disableMobileSearch'],
  data() {
    return {
      searchClient: algoliasearch(this.appId, this.searchApiKey),
    };
  },
  computed: {
    indexName() {
      return `${this.environment}_headerSearch`;
    },
  },
  watch: {
    /**
     * Open / close search if device width/orientation changes
     */
    isMobileEnabled(isMobileEnabled) {
      if (isMobileEnabled) {
        this.focusSearchInput();
      }
    },
  },
  methods: {
    focusSearchInput() {
      const { searchBox } = this.$refs as {
        searchBox: { $refs: { searchInput: { $refs: { input: HTMLInputElement } } } };
      };
      const { searchInput } = searchBox.$refs;
      const { input } = searchInput.$refs;
      // Focus the search input (using autofocus is only reliable on page load)
      input.focus();
    },
  },
});
</script>
<template>
  <Transition @after-enter="focusSearchInput">
    <div
      v-show="isMobileEnabled || isDesktop"
      id="header-search"
      class="relative order-last col-span-2 lg:-order-none lg:col-auto lg:ml-auto lg:h-13 lg:w-64 lg:overflow-visible"
    >
      <div class="lg:absolute lg:left-0 lg:right-0 lg:top-0 lg:z-50 lg:block">
        <div
          class="shadow-default rounded-lg border border-gtg-grey-200 bg-white focus-within:ring-2 focus-within:ring-gtg-teal-500 focus-within:ring-offset-4 [&.v-enter-active]:transition-opacity [&.v-enter-from]:opacity-0 [&.v-leave-active]:transition-opacity [&.v-leave-to]:opacity-0"
        >
          <ais-instant-search :search-client="searchClient" :index-name="indexName">
            <ais-search-box
              ref="searchBox"
              show-loading-indicator
              placeholder="Search"
              :class-names="{
                'ais-SearchBox-form': 'relative',
                'ais-SearchBox-input':
                  'bg-transparent lg:pl-4 lg:pr-15.5 py-3 border-0 focus:outline-none focus:ring-0 w-full px-4 placeholder:text-gtg-grey-800 [&::-webkit-search-cancel-button]:hidden',
                'ais-SearchBox-reset': '-translate-y-1/2 absolute right-11 top-1/2',
                'ais-SearchBox-submit': '-translate-y-1/2 absolute right-4 top-1/2',
                'ais-SearchBox-loadingIndicator': '-translate-y-1/2 absolute right-4 top-1/2',
              }"
              @keydown.esc="$emit('disableMobileSearch')"
            >
              <template #submit-icon>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  @click.prevent="focusSearchInput"
                >
                  <path
                    d="M17 17L12.3333 12.3333M13.8889 8.44444C13.8889 11.4513 11.4513 13.8889 8.44444 13.8889C5.43756 13.8889 3 11.4513 3 8.44444C3 5.43756 5.43756 3 8.44444 3C11.4513 3 13.8889 5.43756 13.8889 8.44444Z"
                    stroke="#161818"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
            </ais-search-box>
            <ais-stats>
              <template #default="{ nbHits, query }">
                <section v-if="nbHits > 0 && query" class="px-4 pb-5 pt-3.5">
                  <div
                    class="max-h-[21rem] overflow-scroll pr-3 [scrollbar-color:theme(colors.gtg.pink.500)_theme(colors.gtg.grey.200)] [scrollbar-width:thin]"
                  >
                    <ais-state-results>
                      <ais-hits>
                        <template #default="{ items /* , sendEvent, query */ }">
                          <ul class="flex flex-col justify-start divide-y border-t">
                            <li v-for="item in items" :key="item.objectID">
                              <a :href="item.url" class="block py-2 text-base hover:underline">
                                {{ item.title }}
                              </a>
                            </li>
                          </ul>
                        </template>
                      </ais-hits>
                    </ais-state-results>
                  </div>
                </section>
                <section v-else-if="nbHits === 0 && query.length > 0" class="px-4 pb-5 pt-3.5">
                  <p class="border-t py-2 text-sm">No results have been found for {{ query }}.</p>
                </section>
                <!-- Blank for the else as we want no output -->
                <div v-else></div>
              </template>
            </ais-stats>
          </ais-instant-search>
        </div>
      </div>
    </div>
  </Transition>
</template>
